/** @jsxImportSource theme-ui */
import React, { useState } from 'react';
import { Box, Card, Text, Flex } from '@infotrack/zenith-ui';
import { ImportPageHeader } from '../components/importPageHeader';
import { ImportUploadForm } from '../components/importUploadForm';
import { ImportProgressIndicator } from '../components/importProgressIndicator';
import { ImportResultsSummary } from '../components/importResultsSummary';
import { useImportTopFive } from '../hooks/useImportTopFive';
import type { ImportTopFiveRequest, ImportTopFiveResponse } from '../types/import.types';
import {
  pageStyles,
  contentStyles,
  headerTextStyles,
  titleStyles,
  subtitleStyles,
  cardStyles,
  errorStyles,
} from './importPage.styles';

type ImportPageState = 'upload' | 'processing' | 'results';

/**
 * Main page for CSV TopFive import workflow.
 * Manages the three-state workflow: upload → processing → results.
 */
export const ImportPage: React.FC = () => {
  const [pageState, setPageState] = useState<ImportPageState>('upload');
  const [results, setResults] = useState<ImportTopFiveResponse | null>(null);
  const { importTopFive, isUploading, progress, error, resetState } = useImportTopFive();

  const handleDownloadTemplate = () => {
    // Create CSV template with headers and example row
    const headers = ['FirstName', 'Surname', 'Name', 'Weight', 'EmployeeId'];
    const exampleRow = ['John', 'Doe', 'Complete project documentation', '20', 'john.doe'];
    const csvContent = [
      headers.join(','),
      exampleRow.join(','),
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'top5-import-template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (data: ImportTopFiveRequest & { startDate: string; endDate: string }) => {
    setPageState('processing');

    // Pass the selected period dates to the API
    const result = await importTopFive(data.file, data.startDate, data.endDate);

    if (result) {
      setResults(result);
      setPageState('results');
    } else {
      // Error occurred - go back to upload
      setPageState('upload');
    }
  };

  const handleStartNew = () => {
    setPageState('upload');
    setResults(null);
    resetState();
  };

  return (
    <Flex sx={pageStyles} as="main">
      <ImportPageHeader />

      <Box sx={contentStyles} as="section" aria-label="Import TopFives">
        {/* Page Header */}
        <Box sx={headerTextStyles}>
          <Box sx={titleStyles}>
            <Text variant="title" as="h1">Import Top5 from CSV</Text>
          </Box>
          <Box sx={subtitleStyles}>
            <Text variant="medium">Bulk import Top5 goals for multiple staff members using a CSV file</Text>
          </Box>
          <Box sx={{ marginTop: '0.5rem' }}>
            <Text variant="small">
              Need help getting started?{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleDownloadTemplate();
                }}
                style={{
                  color: '#0066cc',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Download CSV template
              </a>
            </Text>
          </Box>
        </Box>

        {/* Main Content Card */}
        <Card
          padding="large"
          borderRadius="medium"
          borderColor="neutral/200"
          boxShadow="medium"
          sx={cardStyles}
        >
          {/* Upload View */}
          {pageState === 'upload' && (
            <ImportUploadForm onSubmit={handleSubmit} isUploading={isUploading} />
          )}

          {/* Processing View */}
          {pageState === 'processing' && <ImportProgressIndicator progress={progress} />}

          {/* Results View */}
          {pageState === 'results' && results && (
            <ImportResultsSummary results={results} onStartNew={handleStartNew} />
          )}

          {/* Error Display */}
          {error && pageState === 'upload' && (
            <Box sx={errorStyles}>
              <Text variant="medium">{error}</Text>
            </Box>
          )}
        </Card>
      </Box>
    </Flex>
  );
};
